import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule, TouchedChangeEvent } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Subject, startWith, takeUntil } from 'rxjs';
import { Guid } from '@modules/shared/helpers/guid-helper';
import { FormSelectOption } from '@components/traffic-deviations/components/form-select/interfaces/form-select-option';
import { TranslateService } from '@modules/core/services/translate.service';

@Component({
  selector: 'app-form-select-with-errors',
  imports: [CommonModule, TranslateModule, ReactiveFormsModule],
  templateUrl: './form-select-with-errors.component.html',
  styleUrls: ['./form-select-with-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormSelectWithErrorsComponent implements OnInit, OnDestroy {
  @ViewChild('wrapper', { static: false }) wrapper: ElementRef;
  @ViewChildren('optionItem') optionItems: QueryList<ElementRef>;

  options$ = input.required<BehaviorSubject<FormSelectOption[]>>();
  control = input.required<FormControl<string | number>>();
  label = input<string>('');
  id = input<string>(Guid.newGuid());
  disabled = input<boolean>(false);
  placeholder = input<string>('Choose an option');
  showValidation = input<boolean>(true);

  private readonly _translate = inject(TranslateService);
  private readonly _cdr = inject(ChangeDetectorRef);

  protected selectActive = false;
  protected searchInput = '';
  protected filteredOptions: FormSelectOption[] = [];
  private searchBuffer = '';
  private searchTimeout: any;
  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.control().valueChanges.pipe(
      startWith(this.control().value),
      takeUntil(this._destroy$)
    ).subscribe((selectedValue) => {
      const options = this.options$().value;
      const updatedOptions = options.map((option) => {
        if (option.value !== selectedValue && option.selected) {
          return { ...option, selected: false };
        }
        if (option.value === selectedValue && !option.selected) {
          return { ...option, selected: true };
        }
        return option;
      });
      this.options$().next(updatedOptions);
    });

    // markAsTouched() does not emit statusChanges or valueChanges, so OnPush
    // would never re-check the view. Subscribe to the control's events stream
    // and explicitly mark for check when the touched state is set to true.
    this.control().events.pipe(
      takeUntil(this._destroy$)
    ).subscribe((event) => {
      if (event instanceof TouchedChangeEvent) {
        this._cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.selectActive && this.wrapper && !this.wrapper.nativeElement.contains(event.target)) {
      this.selectActive = false;
    }
  }

  protected get shouldShowError(): boolean {
    const ctrl = this.control();
    return this.showValidation() && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  protected get errorMessage(): string | null {
    const ctrl = this.control();
    if (!this.shouldShowError) return null;

    if (ctrl.hasError('required')) {
      return this._translate.instant('This field is required');
    }

    return this._translate.instant('This field is invalid');
  }

  protected get selectedOption(): FormSelectOption | undefined {
    const options = this.options$().value;
    return options.find(opt => opt.selected);
  }

  protected get selectedText(): string {
    const selected = this.selectedOption;
    return selected ? selected.text : this.placeholder();
  }

  protected onChangeSelect(option: FormSelectOption): void {
    this.control().setValue(option.value);
    this.control().markAsTouched();
    this.selectActive = false;
  }

  protected onKeydownSelect(event: KeyboardEvent): void {
    if (this.disabled()) return;

    if (event.code === 'Enter' || event.code === 'Space') {
      if (!this.selectActive) {
        this.selectActive = true;
        this.searchInput = '';
        this.updateFilteredOptions();
        // Focus the first option after dropdown opens
        setTimeout(() => this.focusSelectedOption());
      } else {
        const focusedElement = document.activeElement as HTMLElement;
        const focusedIndex = Array.from(this.optionItems).findIndex(item => item.nativeElement === focusedElement);
        if (focusedIndex >= 0) {
          this.control().setValue(this.filteredOptions[focusedIndex].value);
          this.control().markAsTouched();
        }
        this.selectActive = false;
      }
      event.stopPropagation();
      event.preventDefault();
    }
    if (event.code === 'Escape' && this.selectActive) {
      this.selectActive = false;
      event.stopPropagation();
    }
    
    if (this.selectActive) {
      const focusedElement = document.activeElement as HTMLElement;
      const currentIndex = Array.from(this.optionItems).findIndex(item => item.nativeElement === focusedElement);
      const itemCount = this.optionItems.length;

      if (event.code === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = currentIndex < itemCount - 1 ? currentIndex + 1 : 0;
        this.optionItems.toArray()[nextIndex].nativeElement.focus();
      }

      if (event.code === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : itemCount - 1;
        this.optionItems.toArray()[prevIndex].nativeElement.focus();
      }
    }
  }

  protected onClickSelect(): void {
    if (!this.disabled()) {
      this.selectActive = !this.selectActive;
      if (this.selectActive) {
        this.searchInput = '';
        this.searchBuffer = '';
        this.updateFilteredOptions();
        setTimeout(() => {
          const input = this.wrapper?.nativeElement.querySelector('.form-select-with-errors__search-input');
          input?.focus();
        });
      }
    }
  }

  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchInput = input.value;
    this.updateFilteredOptions();
  }

  protected onKeydownSearchInput(event: KeyboardEvent): void {
    if (event.code === 'ArrowDown') {
      event.preventDefault();
      const firstOption = this.filteredOptions[0];
      if (firstOption) {
        this.focusOptionByValue(firstOption.value);
      }
    }
    
    if (event.code === 'ArrowUp') {
      event.preventDefault();
      const lastOption = this.filteredOptions[this.filteredOptions.length - 1];
      if (lastOption) {
        this.focusOptionByValue(lastOption.value);
      }
    }

    if (event.code === 'Enter') {
      event.preventDefault();
      const firstOption = this.filteredOptions[0];
      if (firstOption) {
        this.onChangeSelect(firstOption);
      }
    }

    if (event.code === 'Escape') {
      this.selectActive = false;
      this.searchInput = '';
    }
  }

  private updateFilteredOptions(): void {
    const searchTerm = this.searchInput.toLowerCase();
    const selectableOptions = this.options$().value.filter(o => !o.disabled);

    if (!searchTerm) {
      this.filteredOptions = selectableOptions;
    } else {
      this.filteredOptions = selectableOptions.filter(option =>
        option.text.toLowerCase().includes(searchTerm)
      );
    }
  }

  private focusOptionByValue(value: string | number): void {
    const index = this.filteredOptions.findIndex(opt => opt.value === value);
    if (index >= 0 && this.optionItems.length > index) {
      setTimeout(() => {
        this.optionItems.toArray()[index].nativeElement.focus();
      });
    }
  }

  protected onKeydownOption(event: KeyboardEvent): void {
    const currentElement = document.activeElement as HTMLElement;
    const currentIndex = Array.from(this.optionItems).findIndex(item => item.nativeElement === currentElement);
    
    if (event.code === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = currentIndex < this.filteredOptions.length - 1 ? currentIndex + 1 : 0;
      this.optionItems.toArray()[nextIndex].nativeElement.focus();
    }
    
    if (event.code === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : this.filteredOptions.length - 1;
      this.optionItems.toArray()[prevIndex].nativeElement.focus();
    }
  }

  protected onKeydownOptionPrintable(event: KeyboardEvent): void {
    const isPrintable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    
    if (isPrintable) {
      event.preventDefault();
      this.searchInput += event.key;
      this.updateFilteredOptions();
      
      // Focus back to search input
      setTimeout(() => {
        const input = this.wrapper?.nativeElement.querySelector('.form-select-with-errors__search-input');
        input?.focus();
      });
    }
  }

  protected onClearSearch(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchInput = '';
    this.updateFilteredOptions();
    
    // Focus back to search input
    setTimeout(() => {
      const input = this.wrapper?.nativeElement.querySelector('.form-select-with-errors__search-input');
      input?.focus();
    });
  }

  protected trackByValue(index: number, option: FormSelectOption): string | number {
    return option.value;
  }

  private getIndexByLetter(filter: string, startIndex: number): number {
    const options = this.options$().value;
    const orderedOptions = [
      ...options.slice(startIndex),
      ...options.slice(0, startIndex),
    ];
    
    const firstMatch = this.filterOptions(orderedOptions, filter)[0];
    const allSameLetter = (array: string[]) => array.every((letter) => letter === array[0]);

    // first check if there is an exact match for the typed string
    if (firstMatch) {
      return options.indexOf(firstMatch);
    }

    // if the same letter is being repeated, cycle through first-letter matches
    if (allSameLetter(filter.split(''))) {
      const matches = this.filterOptions(orderedOptions, filter[0]);
      if (matches.length > 0) {
        return options.indexOf(matches[0]);
      }
    }

    // if no matches, return -1
    return -1;
  }

  private filterOptions(options: FormSelectOption[], filter: string): FormSelectOption[] {
    return options.filter(option => 
      option.text.toLowerCase().startsWith(filter.toLowerCase())
    );
  }

  private clearSearchTimeout(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  private focusSelectedOption(): void {
    const selectedIndex = this.filteredOptions.findIndex(opt => opt.selected);
    if (selectedIndex >= 0 && this.optionItems.length > selectedIndex) {
      this.optionItems.toArray()[selectedIndex].nativeElement.focus();
    }
  }
}
